(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-vmax-card-scanner]';

  function VmaxCardScanner(root) {
    this.root = root;
    this.config = this.readConfig();
    this.strings = this.config.strings || {};
    this.video = root.querySelector('[data-vmax-video]');
    this.viewer = root.querySelector('[data-vmax-viewer]');
    this.startButton = root.querySelector('[data-vmax-start]');
    this.torchButton = root.querySelector('[data-vmax-torch]');
    this.closeCameraButton = root.querySelector('[data-vmax-close-camera]');
    this.zoomControl = root.querySelector('[data-vmax-zoom-control]');
    this.zoomInput = root.querySelector('[data-vmax-zoom]');
    this.zoomValue = root.querySelector('[data-vmax-zoom-value]');
    this.scanSuccess = root.querySelector('[data-vmax-scan-success]');
    this.status = root.querySelector('[data-vmax-status]');
    this.statusTitle = root.querySelector('[data-vmax-status-title]');
    this.statusDetail = root.querySelector('[data-vmax-status-detail]');
    this.mode = root.querySelector('[data-vmax-mode]');
    this.history = root.querySelector('[data-vmax-history]');
    this.resultsList = root.querySelector('[data-vmax-results-list]');
    this.resultTemplate = root.querySelector('[data-vmax-result-template]');
    this.resultCount = root.querySelector('[data-vmax-result-count]');
    this.clearButton = root.querySelector('[data-vmax-clear]');
    this.nextButton = root.querySelector('[data-vmax-next]');
    this.result = null;
    this.productImage = null;
    this.productTitle = null;
    this.variantTitle = null;
    this.condition = null;
    this.price = null;
    this.comparePrice = null;
    this.availability = null;
    this.variantPicker = null;
    this.variantSelect = null;
    this.addButton = null;
    this.addButtonLabel = null;
    this.cartFeedback = null;
    this.cartMessage = null;

    this.stream = null;
    this.decoder = null;
    this.decoderControls = null;
    this.decoderToken = 0;
    this.operationToken = 0;
    this.phase = 'idle';
    this.torchOn = false;
    this.zoomMode = 'css';
    this.scanSuccessTimer = null;
    this.currentProduct = null;
    this.currentVariant = null;
    this.currentPayload = null;
    this.currentEntry = null;
    this.scanEntries = new Map();
    this.scanSequence = 0;
    this.lastInvalidCode = '';
    this.lastInvalidCodeAt = 0;

    this.boundPageHide = this.stopCamera.bind(this, false);
    this.bindEvents();
  }

  VmaxCardScanner.prototype.readConfig = function () {
    var configElement = this.root.querySelector('[data-vmax-scanner-config]');

    if (!configElement) {
      return {};
    }

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('VMAX Scanner: configuration invalide.', error);
      return {};
    }
  };

  VmaxCardScanner.prototype.bindEvents = function () {
    var self = this;

    this.startButton.addEventListener('click', function () {
      self.startCamera();
    });

    this.closeCameraButton.addEventListener('click', function () {
      self.stopCamera(true);
    });

    this.torchButton.addEventListener('click', function () {
      self.toggleTorch();
    });

    if (this.zoomInput) {
      this.zoomInput.addEventListener('input', function (event) {
        self.applyZoom(event.target.value);
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', function () {
        self.scanAnother();
      });
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', function () {
        self.clearHistory();
      });
    }

    if (this.resultsList) {
      this.resultsList.addEventListener('click', function (event) {
        var button = event.target.closest('[data-vmax-add]');

        if (button && self.resultsList.contains(button)) {
          self.addToCart(button);
        }
      });

      this.resultsList.addEventListener('change', function (event) {
        var select = event.target.closest('[data-vmax-variant-select]');
        var entry;
        var variant;

        if (!select || !self.resultsList.contains(select)) {
          return;
        }

        entry = self.findEntryFromElement(select);
        variant = entry ? self.findVariantInProduct(entry.product, select.value) : null;

        if (entry && variant) {
          self.applyVariantToEntry(entry, variant, 'manual', entry === self.currentEntry);
        }
      });
    }

    window.addEventListener('pagehide', this.boundPageHide);
  };

  VmaxCardScanner.prototype.startCamera = async function () {
    var constraints;

    if (!window.isSecureContext) {
      this.showError(
        this.strings.secure_title,
        this.strings.secure_detail
      );
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.showError(
        this.strings.unsupported_title,
        this.strings.unsupported_detail
      );
      return;
    }

    if (!window.ZXingBrowser || !window.ZXingBrowser.BrowserMultiFormatReader) {
      this.showError(
        this.strings.reader_unavailable,
        this.strings.reader_unavailable_detail
      );
      return;
    }

    this.startButton.disabled = true;
    this.setMode(this.strings.permission);
    this.setStatus(this.strings.opening, this.strings.opening_detail, 'active');

    try {
      if (!this.stream) {
        constraints = {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 960 }
          }
        };

        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.video.srcObject = this.stream;
        this.video.setAttribute('playsinline', '');
        this.video.muted = true;
        await this.video.play();
      }

      this.root.classList.add('is-camera-active');
      this.startButton.hidden = true;
      this.closeCameraButton.hidden = false;
      this.startButton.disabled = false;
      this.configureTorchButton();
      this.configureZoomControl();
      await this.startQrDecoder();
    } catch (error) {
      this.startButton.disabled = false;
      this.handleCameraError(error);
      this.releaseStream();
    }
  };

  VmaxCardScanner.prototype.startQrDecoder = async function () {
    var self = this;

    this.phase = 'scanning-qr';
    this.root.classList.add('is-scanning');
    this.setMode(this.strings.qr_phase);
    this.setStatus(
      this.strings.qr_title,
      this.strings.qr_detail,
      'active'
    );

    await this.startDecoder('qr', function (text, controls) {
      var payload = self.parseProductPayload(text);

      if (!payload) {
        self.reportInvalidQr(text);
        return false;
      }

      controls.stop();
      self.decoderControls = null;
      self.decoderToken += 1;
      self.root.classList.remove('is-scanning');
      self.showScanSuccess();
      self.processProductPayload(payload);
      return true;
    });
  };

  VmaxCardScanner.prototype.startBarcodeDecoder = async function () {
    var self = this;

    if (!this.stream) {
      return;
    }

    this.phase = 'scanning-barcode';
    this.root.classList.add('is-scanning');
    this.setMode(this.strings.barcode_phase);
    this.setStatus(
      this.strings.barcode_title,
      this.strings.barcode_detail,
      'active'
    );

    await this.startDecoder('barcode', function (text, controls) {
      var variant = self.findVariantByBarcode(text);

      if (!variant) {
        self.reportInvalidBarcode(text);
        return false;
      }

      controls.stop();
      self.decoderControls = null;
      self.decoderToken += 1;
      self.root.classList.remove('is-scanning');
      self.showScanSuccess();
      self.selectVariant(variant, 'barcode');
      return true;
    });
  };

  VmaxCardScanner.prototype.startDecoder = async function (kind, onResult) {
    var self = this;
    var token;
    var callbackStopped = false;
    var formats;

    this.stopDecoder();
    token = this.decoderToken;

    try {
      this.decoder = new window.ZXingBrowser.BrowserMultiFormatReader(undefined, {
        delayBetweenScanAttempts: 120,
        delayBetweenScanSuccess: 450,
        tryPlayVideoTimeout: 5000
      });

      formats = kind === 'qr'
        ? [window.ZXingBrowser.BarcodeFormat.QR_CODE]
        : [window.ZXingBrowser.BarcodeFormat.CODE_128];

      this.decoder.possibleFormats = formats;

      var controls = await this.decoder.decodeFromVideoElement(
        this.video,
        function (result, error, callbackControls) {
          if (token !== self.decoderToken || callbackStopped) {
            return;
          }

          if (result) {
            callbackStopped = onResult(result.getText(), callbackControls) === true;
            return;
          }

          if (error && !self.isRoutineDecodeError(error)) {
            console.warn('VMAX Scanner: erreur de décodage.', error);
          }
        }
      );

      if (callbackStopped || token !== this.decoderToken) {
        controls.stop();
      } else {
        this.decoderControls = controls;
      }
    } catch (error) {
      if (token === this.decoderToken) {
        this.showError(this.strings.reader_interrupted, this.strings.reader_interrupted_detail);
        console.error('VMAX Scanner: impossible de démarrer le lecteur.', error);
      }
    }
  };

  VmaxCardScanner.prototype.stopDecoder = function () {
    this.decoderToken += 1;

    if (this.decoderControls && typeof this.decoderControls.stop === 'function') {
      try {
        this.decoderControls.stop();
      } catch (error) {
        console.warn('VMAX Scanner: arrêt du lecteur incomplet.', error);
      }
    }

    this.decoderControls = null;
    this.decoder = null;
    this.root.classList.remove('is-scanning');
  };

  VmaxCardScanner.prototype.processProductPayload = async function (payload) {
    var requestToken = ++this.operationToken;
    var availableVariants;
    var requestedVariant;

    this.stopDecoder();
    this.phase = 'loading';
    this.currentPayload = payload;
    this.currentProduct = null;
    this.currentVariant = null;
    this.setMode(this.strings.checking_price);
    this.setStatus(this.strings.recognized, this.strings.fetching_price, 'active');
    this.resetCartFeedback();

    try {
      var product = await this.fetchProduct(payload.handle);

      if (requestToken !== this.operationToken) {
        return;
      }

      if (!product || !Array.isArray(product.variants) || product.variants.length === 0) {
        throw new Error(this.strings.no_variants);
      }

      this.currentProduct = product;
      this.renderProduct(product);

      requestedVariant = payload.variantId ? this.findVariantById(payload.variantId) : null;
      availableVariants = product.variants.filter(function (variant) {
        return Boolean(variant.available);
      });

      if (requestedVariant) {
        this.selectVariant(requestedVariant, 'qr-variant');
        return;
      }

      if (availableVariants.length === 1) {
        this.selectVariant(availableVariants[0], 'only-available');
        return;
      }

      if (availableVariants.length === 0) {
        this.selectVariant(product.variants[0], 'sold-out');
        return;
      }

      this.renderVariantPicker(product.variants);
      this.clearVariantDetails();

      if (this.stream) {
        await this.startBarcodeDecoder();
      } else {
        this.phase = 'choose-variant';
        this.setMode(this.strings.choose_condition);
        this.setStatus(
          this.strings.multiple_conditions,
          this.strings.choose_condition_detail,
          'neutral'
        );
      }
    } catch (error) {
      if (requestToken !== this.operationToken) {
        return;
      }

      this.showError(
        this.strings.not_found,
        error && error.message ? error.message : this.strings.fetch_failed
      );
    }
  };

  VmaxCardScanner.prototype.fetchProduct = async function (handle) {
    var rootUrl = this.normalizedRootUrl();
    var endpoint = rootUrl + 'products/' + encodeURIComponent(handle) + '.js?vmax_scan=' + Date.now();
    var response = await fetch(endpoint, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(this.strings.unpublished);
      }

      throw new Error(this.strings.price_failed);
    }

    return response.json();
  };

  VmaxCardScanner.prototype.parseProductPayload = function (rawValue) {
    var value = String(rawValue || '').trim();
    var url;
    var match;
    var host;
    var allowedHosts;
    var handle;

    if (!value) {
      return null;
    }

    try {
      url = new URL(value, window.location.origin);
    } catch (error) {
      return null;
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return null;
    }

    host = this.normalizeHost(url.hostname);
    allowedHosts = (this.config.allowedHosts || [])
      .concat([window.location.hostname])
      .map(this.normalizeHost.bind(this))
      .filter(Boolean);

    if (allowedHosts.indexOf(host) === -1) {
      return null;
    }

    match = url.pathname.match(/\/products\/([^/?#]+)/i);

    if (!match || !match[1]) {
      return null;
    }

    try {
      handle = decodeURIComponent(match[1]);
    } catch (error) {
      handle = match[1];
    }

    return {
      handle: handle,
      variantId: url.searchParams.get('variant'),
      raw: value
    };
  };

  VmaxCardScanner.prototype.normalizeHost = function (host) {
    return String(host || '')
      .toLowerCase()
      .replace(/^www\./, '')
      .replace(/:\d+$/, '');
  };

  VmaxCardScanner.prototype.renderProduct = function (product) {
    var imageUrl = this.resolveImageUrl(product.featured_image || (product.images && product.images[0]));
    var element;
    var entry;
    var refs;

    if (!this.resultTemplate || !this.resultTemplate.content || !this.resultsList) {
      throw new Error(this.strings.history_failed);
    }

    element = this.resultTemplate.content.firstElementChild.cloneNode(true);
    this.scanSequence += 1;
    element.dataset.vmaxScanId = String(this.scanSequence);

    refs = {
      result: element,
      productImage: element.querySelector('[data-vmax-product-image]'),
      productTitle: element.querySelector('[data-vmax-product-title]'),
      variantTitle: element.querySelector('[data-vmax-variant-title]'),
      condition: element.querySelector('[data-vmax-condition]'),
      price: element.querySelector('[data-vmax-price]'),
      comparePrice: element.querySelector('[data-vmax-compare-price]'),
      availability: element.querySelector('[data-vmax-availability]'),
      variantPicker: element.querySelector('[data-vmax-variant-picker]'),
      variantSelect: element.querySelector('[data-vmax-variant-select]'),
      addButton: element.querySelector('[data-vmax-add]'),
      addButtonLabel: element.querySelector('[data-vmax-add-label]'),
      cartFeedback: element.querySelector('[data-vmax-cart-feedback]'),
      cartMessage: element.querySelector('[data-vmax-cart-message]')
    };

    entry = {
      id: String(this.scanSequence),
      element: element,
      product: product,
      variant: null,
      refs: refs
    };

    this.scanEntries.set(entry.id, entry);
    this.currentEntry = entry;
    this.assignActiveEntry(entry);
    this.resultsList.appendChild(element);
    this.updateHistoryUi();

    this.nextButton.hidden = false;
    this.productTitle.textContent = product.title || this.strings.untitled;
    this.variantTitle.textContent = this.strings.reading_condition;
    this.productImage.src = imageUrl;
    this.productImage.alt = product.title || this.strings.card_image;
    this.productImage.hidden = !imageUrl;
    this.condition.hidden = true;
    this.variantPicker.hidden = true;
    this.addButton.disabled = true;
    this.setAddButtonLabel(this.strings.add_to_cart);
    this.availability.classList.remove('is-sold-out');
    this.availability.textContent = this.strings.checking_availability;
    this.maybeBringResultIntoView();

    return entry;
  };

  VmaxCardScanner.prototype.assignActiveEntry = function (entry) {
    var refs = entry && entry.refs ? entry.refs : {};

    this.result = refs.result || null;
    this.productImage = refs.productImage || null;
    this.productTitle = refs.productTitle || null;
    this.variantTitle = refs.variantTitle || null;
    this.condition = refs.condition || null;
    this.price = refs.price || null;
    this.comparePrice = refs.comparePrice || null;
    this.availability = refs.availability || null;
    this.variantPicker = refs.variantPicker || null;
    this.variantSelect = refs.variantSelect || null;
    this.addButton = refs.addButton || null;
    this.addButtonLabel = refs.addButtonLabel || null;
    this.cartFeedback = refs.cartFeedback || null;
    this.cartMessage = refs.cartMessage || null;
  };

  VmaxCardScanner.prototype.findEntryFromElement = function (element) {
    var result = element && element.closest ? element.closest('[data-vmax-scan-id]') : null;

    if (!result) {
      return null;
    }

    return this.scanEntries.get(String(result.dataset.vmaxScanId)) || null;
  };

  VmaxCardScanner.prototype.updateHistoryUi = function () {
    var count = this.scanEntries.size;

    if (this.history) {
      this.history.hidden = count === 0;
    }

    if (this.resultCount) {
      this.resultCount.textContent = '(' + count + ')';
    }
  };

  VmaxCardScanner.prototype.renderVariantPicker = function (variants, entry) {
    var self = this;
    var placeholder = document.createElement('option');
    var target = entry || this.currentEntry;
    var select = target && target.refs ? target.refs.variantSelect : this.variantSelect;
    var picker = target && target.refs ? target.refs.variantPicker : this.variantPicker;

    if (!select || !picker) {
      return;
    }

    select.innerHTML = '';
    placeholder.value = '';
    placeholder.textContent = this.strings.select_condition;
    placeholder.selected = true;
    select.appendChild(placeholder);

    variants.forEach(function (variant) {
      var option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent = variant.title + ' — ' + self.formatMoney(variant.price) + (variant.available ? '' : self.strings.sold_out_suffix);
      select.appendChild(option);
    });

    picker.hidden = false;
  };

  VmaxCardScanner.prototype.clearVariantDetails = function (entry) {
    var target = entry || this.currentEntry;
    var refs = target && target.refs ? target.refs : null;

    if (!refs) {
      return;
    }

    target.variant = null;

    if (target === this.currentEntry) {
      this.currentVariant = null;
    }

    refs.variantTitle.textContent = this.strings.confirm_condition;
    refs.condition.hidden = true;
    refs.price.textContent = '—';
    refs.comparePrice.hidden = true;
    refs.availability.classList.remove('is-sold-out');
    refs.availability.textContent = this.strings.multiple_available;
    refs.addButton.disabled = true;
  };

  VmaxCardScanner.prototype.selectVariant = function (variant, source) {
    if (!this.currentEntry) {
      return;
    }

    this.applyVariantToEntry(this.currentEntry, variant, source, true);
  };

  VmaxCardScanner.prototype.applyVariantToEntry = function (entry, variant, source, isActiveScan) {
    var refs = entry.refs;
    var product = entry.product;
    var conditionInfo = this.getConditionInfo(variant.title);
    var variantImage = this.resolveImageUrl(
      variant.featured_image ||
      (product && product.featured_image) ||
      (product && product.images && product.images[0])
    );
    var compareAtPrice = Number(variant.compare_at_price || 0);
    var price = Number(variant.price || 0);
    var availableVariantCount = product
      ? product.variants.filter(function (item) { return Boolean(item.available); }).length
      : 0;

    entry.variant = variant;
    this.resetCartFeedback(entry);

    if (isActiveScan) {
      this.stopDecoder();
      this.phase = 'result';
      this.currentProduct = product;
      this.currentVariant = variant;
      this.root.classList.remove('is-scanning');
      this.setMode(this.strings.price_current);
    }

    refs.variantTitle.textContent = variant.title || this.strings.standard_variant;
    refs.price.textContent = this.formatMoney(price);

    if (variantImage) {
      refs.productImage.src = variantImage;
      refs.productImage.hidden = false;
    }

    refs.condition.textContent = conditionInfo.label;
    refs.condition.dataset.condition = conditionInfo.key;
    refs.condition.hidden = false;

    if (compareAtPrice > price) {
      refs.comparePrice.textContent = this.formatMoney(compareAtPrice);
      refs.comparePrice.hidden = false;
    } else {
      refs.comparePrice.hidden = true;
    }

    if (availableVariantCount > 1) {
      if (refs.variantPicker.hidden) {
        this.renderVariantPicker(product.variants, entry);
      }
      refs.variantSelect.value = String(variant.id);
    } else {
      refs.variantPicker.hidden = true;
    }

    if (variant.available) {
      refs.availability.classList.remove('is-sold-out');
      refs.availability.textContent = this.strings.in_stock;
      refs.addButton.disabled = false;
      this.setAddButtonLabel(this.strings.add_to_cart, entry);

      if (isActiveScan) {
        this.setStatus(
          source === 'barcode' ? this.strings.barcode_confirmed : this.strings.price_verified,
          conditionInfo.longLabel + ' · ' + this.formatMoney(price),
          'success'
        );
        this.vibrate([40]);
      }
    } else {
      refs.availability.classList.add('is-sold-out');
      refs.availability.textContent = this.strings.variant_sold_out;
      refs.addButton.disabled = true;

      if (isActiveScan) {
        this.setStatus(
          this.strings.sold_out,
          this.strings.condition_unavailable,
          'error'
        );
      }
    }

    if (isActiveScan) {
      this.maybeBringResultIntoView();
    }
  };

  VmaxCardScanner.prototype.addToCart = async function (button) {
    var entry = button ? this.findEntryFromElement(button) : this.currentEntry;
    var refs = entry && entry.refs ? entry.refs : null;
    var variant = entry ? entry.variant : null;
    var defaultLabel = this.strings.add_to_cart;
    var response;
    var data;

    if (!entry || !refs || !variant || !variant.available || refs.addButton.disabled) {
      return;
    }

    refs.addButton.disabled = true;
    this.setAddButtonLabel(this.strings.adding, entry);
    this.resetCartFeedback(entry);

    try {
      response = await fetch(this.config.cartAddUrl || this.normalizedRootUrl() + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variant.id),
              quantity: 1
            }
          ]
        })
      });

      data = await response.json().catch(function () { return {}; });

      if (!response.ok) {
        throw new Error(data.description || data.message || this.strings.cart_refused);
      }

      this.setAddButtonLabel(this.strings.added, entry);
      this.showCartFeedback(this.strings.cart_success, 'success', entry);
      this.setStatus(this.strings.add_confirmed, this.strings.scan_next, 'success');
      this.updateThemeCart();
      this.vibrate([45, 60, 45]);
    } catch (error) {
      refs.addButton.disabled = false;
      this.setAddButtonLabel(defaultLabel, entry);
      this.showCartFeedback(
        error && error.message ? error.message : this.strings.cart_failed,
        'error',
        entry
      );
    }
  };

  VmaxCardScanner.prototype.updateThemeCart = function () {
    var self = this;

    try {
      if (window.Shopify && typeof window.Shopify.adjustCartDropDown === 'function') {
        window.Shopify.adjustCartDropDown();
      }
    } catch (error) {
      console.warn('VMAX Scanner: le mini-panier du thème n’a pas pu être actualisé.', error);
    }

    fetch(this.normalizedRootUrl() + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-qty').forEach(function (element) {
          element.textContent = String(cart.item_count);
        });

        self.root.dispatchEvent(new CustomEvent('vmax:cart-updated', {
          bubbles: true,
          detail: { cart: cart }
        }));
      })
      .catch(function () {
        // L’ajout est déjà confirmé; l’échec de ce rafraîchissement visuel n’est pas bloquant.
      });
  };

  VmaxCardScanner.prototype.scanAnother = async function () {
    this.operationToken += 1;
    this.currentProduct = null;
    this.currentVariant = null;
    this.currentPayload = null;
    this.currentEntry = null;
    this.assignActiveEntry(null);
    this.nextButton.hidden = true;

    if (this.stream) {
      await this.startQrDecoder();
    } else {
      await this.startCamera();
    }
  };

  VmaxCardScanner.prototype.clearHistory = async function () {
    this.operationToken += 1;
    this.stopDecoder();
    this.scanEntries.clear();
    this.resultsList.innerHTML = '';
    this.currentProduct = null;
    this.currentVariant = null;
    this.currentPayload = null;
    this.currentEntry = null;
    this.assignActiveEntry(null);
    this.updateHistoryUi();
    this.nextButton.hidden = true;

    if (this.stream) {
      await this.startQrDecoder();
    } else if (!this.stream) {
      this.phase = 'idle';
      this.setMode(this.strings.ready);
      this.setStatus(this.strings.cleared, this.strings.restart, 'neutral');
    }
  };

  VmaxCardScanner.prototype.findVariantById = function (id) {
    return this.findVariantInProduct(this.currentProduct, id);
  };

  VmaxCardScanner.prototype.findVariantInProduct = function (product, id) {
    if (!product || !Array.isArray(product.variants)) {
      return null;
    }

    return product.variants.find(function (variant) {
      return String(variant.id) === String(id);
    }) || null;
  };

  VmaxCardScanner.prototype.findVariantByBarcode = function (barcode) {
    var normalizedBarcode = String(barcode || '').trim();

    if (!this.currentProduct || !normalizedBarcode) {
      return null;
    }

    return this.currentProduct.variants.find(function (variant) {
      return String(variant.barcode || '').trim() === normalizedBarcode;
    }) || null;
  };

  VmaxCardScanner.prototype.getConditionInfo = function (title) {
    var normalized = String(title || '').toLowerCase();

    if (normalized.indexOf('near mint') !== -1) {
      return { key: 'nm', label: 'NM', longLabel: 'Near Mint' };
    }
    if (normalized.indexOf('lightly played') !== -1) {
      return { key: 'lp', label: 'LP', longLabel: 'Lightly Played' };
    }
    if (normalized.indexOf('moderately played') !== -1) {
      return { key: 'mp', label: 'MP', longLabel: 'Moderately Played' };
    }
    if (normalized.indexOf('heavily played') !== -1) {
      return { key: 'hp', label: 'HP', longLabel: 'Heavily Played' };
    }
    if (normalized.indexOf('damaged') !== -1) {
      return { key: 'dmg', label: 'DMG', longLabel: 'Damaged' };
    }

    return { key: 'other', label: this.strings.card_label, longLabel: title || this.strings.standard_variant };
  };

  VmaxCardScanner.prototype.resolveImageUrl = function (image) {
    var source = image;

    if (image && typeof image === 'object') {
      source = image.src || image.url;
    }

    if (!source) {
      return '';
    }

    source = String(source);

    if (source.indexOf('//') === 0) {
      return window.location.protocol + source;
    }

    if (source.indexOf('/') === 0) {
      return window.location.origin + source;
    }

    return source;
  };

  VmaxCardScanner.prototype.formatMoney = function (cents) {
    var amount = Number(cents || 0);
    var moneyFormat = this.config.moneyFormat || '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount, moneyFormat);
    }

    try {
      return new Intl.NumberFormat(this.config.locale, {
        style: 'currency',
        currency: 'CAD'
      }).format(amount / 100);
    } catch (error) {
      return (amount / 100).toFixed(2) + ' $';
    }
  };

  VmaxCardScanner.prototype.normalizedRootUrl = function () {
    var rootUrl = String(this.config.rootUrl || '/');

    if (rootUrl.charAt(0) !== '/') {
      rootUrl = '/' + rootUrl;
    }

    if (rootUrl.charAt(rootUrl.length - 1) !== '/') {
      rootUrl += '/';
    }

    return rootUrl;
  };

  VmaxCardScanner.prototype.configureTorchButton = function () {
    var track = this.stream && this.stream.getVideoTracks()[0];
    var capabilities;

    this.torchButton.hidden = true;

    if (!track || typeof track.getCapabilities !== 'function') {
      return;
    }

    try {
      capabilities = track.getCapabilities();
      this.torchButton.hidden = !capabilities.torch;
    } catch (error) {
      this.torchButton.hidden = true;
    }
  };

  VmaxCardScanner.prototype.configureZoomControl = function () {
    var track = this.stream && this.stream.getVideoTracks()[0];
    var capabilities;
    var settings;
    var zoomCapability;
    var minimum = 1;
    var maximum = 3;
    var step = 0.1;
    var current = 1;

    if (!this.zoomControl || !this.zoomInput || !this.zoomValue) {
      return;
    }

    this.zoomMode = 'css';

    if (track && typeof track.getCapabilities === 'function') {
      try {
        capabilities = track.getCapabilities();
        settings = (typeof track.getSettings === 'function' ? track.getSettings() : {}) || {};
        zoomCapability = capabilities && capabilities.zoom;

        if (
          zoomCapability &&
          Number.isFinite(Number(zoomCapability.min)) &&
          Number.isFinite(Number(zoomCapability.max)) &&
          Number(zoomCapability.max) > Number(zoomCapability.min)
        ) {
          minimum = Number(zoomCapability.min);
          maximum = Number(zoomCapability.max);
          step = Number(zoomCapability.step) > 0 ? Number(zoomCapability.step) : 0.1;
          current = Number.isFinite(Number(settings.zoom)) ? Number(settings.zoom) : minimum;
          this.zoomMode = 'track';
        }
      } catch (error) {
        this.zoomMode = 'css';
      }
    }

    this.zoomInput.min = String(minimum);
    this.zoomInput.max = String(maximum);
    this.zoomInput.step = String(step);
    this.zoomInput.value = String(Math.min(maximum, Math.max(minimum, current)));
    this.zoomControl.hidden = false;
    this.applyZoom(this.zoomInput.value);
  };

  VmaxCardScanner.prototype.applyZoom = async function (rawValue) {
    var track = this.stream && this.stream.getVideoTracks()[0];
    var minimum = Number(this.zoomInput && this.zoomInput.min) || 1;
    var maximum = Number(this.zoomInput && this.zoomInput.max) || 3;
    var value = Math.min(maximum, Math.max(minimum, Number(rawValue) || minimum));

    if (!this.zoomInput || !this.zoomValue || !this.video) {
      return;
    }

    this.zoomInput.value = String(value);
    this.zoomValue.textContent = new Intl.NumberFormat(this.config.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value) + '×';

    if (this.zoomMode === 'track' && track && typeof track.applyConstraints === 'function') {
      try {
        await track.applyConstraints({ advanced: [{ zoom: value }] });
        this.video.style.transform = '';
        return;
      } catch (error) {
        this.zoomMode = 'css';
        this.zoomInput.min = '1';
        this.zoomInput.max = '3';
        this.zoomInput.step = '0.1';
        value = Math.min(3, Math.max(1, value));
        this.zoomInput.value = String(value);
        this.zoomValue.textContent = new Intl.NumberFormat(this.config.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value) + '×';
      }
    }

    this.video.style.transform = 'scale(' + value + ')';
  };

  VmaxCardScanner.prototype.resetZoomControl = function () {
    this.zoomMode = 'css';

    if (this.zoomControl) {
      this.zoomControl.hidden = true;
    }

    if (this.zoomInput) {
      this.zoomInput.min = '1';
      this.zoomInput.max = '3';
      this.zoomInput.step = '0.1';
      this.zoomInput.value = '1';
    }

    if (this.zoomValue) {
      this.zoomValue.textContent = new Intl.NumberFormat(this.config.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(1) + '×';
    }

    if (this.video) {
      this.video.style.transform = '';
    }
  };

  VmaxCardScanner.prototype.toggleTorch = async function () {
    var track = this.stream && this.stream.getVideoTracks()[0];

    if (!track || typeof track.applyConstraints !== 'function') {
      return;
    }

    try {
      this.torchOn = !this.torchOn;
      await track.applyConstraints({ advanced: [{ torch: this.torchOn }] });
      this.torchButton.setAttribute('aria-pressed', this.torchOn ? 'true' : 'false');
      this.torchButton.classList.toggle('is-active', this.torchOn);
    } catch (error) {
      this.torchOn = false;
      this.torchButton.hidden = true;
    }
  };

  VmaxCardScanner.prototype.stopCamera = function (updateStatus) {
    this.stopDecoder();
    this.releaseStream();
    this.phase = 'idle';
    this.root.classList.remove('is-camera-active', 'is-scanning');
    this.startButton.hidden = false;
    this.startButton.disabled = false;
    this.closeCameraButton.hidden = true;
    this.torchButton.hidden = true;
    this.torchButton.setAttribute('aria-pressed', 'false');
    this.torchOn = false;
    this.resetZoomControl();
    this.hideScanSuccess();

    if (updateStatus !== false) {
      this.setMode(this.strings.camera_closed);
      this.setStatus(this.strings.camera_closed, this.strings.resume, 'neutral');
    }
  };

  VmaxCardScanner.prototype.releaseStream = function () {
    if (this.stream) {
      this.stream.getTracks().forEach(function (track) {
        track.stop();
      });
    }

    this.stream = null;

    if (this.video) {
      try {
        this.video.pause();
      } catch (error) {
        // Aucun traitement requis.
      }
      this.video.srcObject = null;
    }
  };

  VmaxCardScanner.prototype.handleCameraError = function (error) {
    var name = error && error.name ? error.name : '';

    if (name === 'NotAllowedError' || name === 'SecurityError') {
      this.showError(
        this.strings.denied,
        this.strings.denied_detail
      );
      return;
    }

    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      this.showError(this.strings.no_camera, this.strings.no_camera_detail);
      return;
    }

    if (name === 'NotReadableError' || name === 'TrackStartError') {
      this.showError(this.strings.camera_busy, this.strings.camera_busy_detail);
      return;
    }

    this.showError(this.strings.camera_failed, this.strings.camera_failed_detail);
    console.error('VMAX Scanner: erreur caméra.', error);
  };

  VmaxCardScanner.prototype.isRoutineDecodeError = function (error) {
    var name = error && (error.name || (error.constructor && error.constructor.name));
    return name === 'NotFoundException' || name === 'ChecksumException' || name === 'FormatException';
  };

  VmaxCardScanner.prototype.reportInvalidQr = function (value) {
    var now = Date.now();

    if (this.lastInvalidCode === value && now - this.lastInvalidCodeAt < 1600) {
      return;
    }

    this.lastInvalidCode = value;
    this.lastInvalidCodeAt = now;
    this.setStatus(
      this.strings.invalid_qr,
      this.strings.invalid_qr_detail,
      'error'
    );
  };

  VmaxCardScanner.prototype.reportInvalidBarcode = function (value) {
    var now = Date.now();

    if (this.lastInvalidCode === value && now - this.lastInvalidCodeAt < 1600) {
      return;
    }

    this.lastInvalidCode = value;
    this.lastInvalidCodeAt = now;
    this.setStatus(
      this.strings.wrong_barcode,
      this.strings.wrong_barcode_detail,
      'error'
    );
  };

  VmaxCardScanner.prototype.setStatus = function (title, detail, tone) {
    this.statusTitle.textContent = title;
    this.statusDetail.textContent = detail;
    this.status.dataset.tone = tone || 'neutral';
  };

  VmaxCardScanner.prototype.setMode = function (text) {
    this.mode.textContent = text;
  };

  VmaxCardScanner.prototype.showError = function (title, detail) {
    this.phase = 'error';
    this.root.classList.remove('is-scanning');
    this.setMode(this.strings.check_required);
    this.setStatus(title, detail, 'error');
  };

  VmaxCardScanner.prototype.showCartFeedback = function (message, tone, entry) {
    var refs = entry && entry.refs ? entry.refs : this;

    if (!refs.cartFeedback || !refs.cartMessage) {
      return;
    }

    refs.cartFeedback.hidden = false;
    refs.cartFeedback.dataset.tone = tone;
    refs.cartMessage.textContent = message;
  };

  VmaxCardScanner.prototype.resetCartFeedback = function (entry) {
    var refs = entry && entry.refs ? entry.refs : this;

    if (!refs.cartFeedback || !refs.cartMessage) {
      return;
    }

    refs.cartFeedback.hidden = true;
    refs.cartFeedback.dataset.tone = '';
    refs.cartMessage.textContent = '';
  };

  VmaxCardScanner.prototype.setAddButtonLabel = function (label, entry) {
    var refs = entry && entry.refs ? entry.refs : this;

    if (refs.addButtonLabel) {
      refs.addButtonLabel.textContent = label;
    }

    if (refs.addButton) {
      refs.addButton.setAttribute('aria-label', label);
      refs.addButton.setAttribute('title', label);
    }
  };

  VmaxCardScanner.prototype.showScanSuccess = function () {
    if (!this.scanSuccess) {
      return;
    }

    window.clearTimeout(this.scanSuccessTimer);
    this.scanSuccess.classList.remove('is-visible');
    this.scanSuccess.setAttribute('aria-hidden', 'false');

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        this.scanSuccess.classList.add('is-visible');
      }.bind(this));
    }.bind(this));

    this.scanSuccessTimer = window.setTimeout(function () {
      this.hideScanSuccess();
    }.bind(this), 900);

    this.vibrate([35]);
  };

  VmaxCardScanner.prototype.hideScanSuccess = function () {
    window.clearTimeout(this.scanSuccessTimer);
    this.scanSuccessTimer = null;

    if (this.scanSuccess) {
      this.scanSuccess.classList.remove('is-visible');
      this.scanSuccess.setAttribute('aria-hidden', 'true');
    }
  };

  VmaxCardScanner.prototype.vibrate = function (pattern) {
    if (navigator.vibrate) {
      try {
        navigator.vibrate(pattern);
      } catch (error) {
        // La vibration est une amélioration facultative.
      }
    }
  };

  VmaxCardScanner.prototype.maybeBringResultIntoView = function () {
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (window.innerWidth < 900 && this.result && typeof this.result.scrollIntoView === 'function') {
      window.setTimeout(function () {
        this.result.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'nearest'
        });
      }.bind(this), 80);
    }
  };

  VmaxCardScanner.prototype.destroy = function () {
    this.operationToken += 1;
    this.stopCamera(false);
    window.removeEventListener('pagehide', this.boundPageHide);
    delete this.root.dataset.vmaxScannerInitialized;
  };

  function initializeWithin(container) {
    var roots = [];

    if (container.matches && container.matches(ROOT_SELECTOR)) {
      roots.push(container);
    }

    if (container.querySelectorAll) {
      roots = roots.concat(Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR)));
    }

    roots.forEach(function (root) {
      if (root.dataset.vmaxScannerInitialized === 'true') {
        return;
      }

      root.dataset.vmaxScannerInitialized = 'true';
      root.vmaxCardScanner = new VmaxCardScanner(root);
    });
  }

  function initialize() {
    initializeWithin(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeWithin(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);

    if (root && root.vmaxCardScanner) {
      root.vmaxCardScanner.destroy();
    }
  });
})();
